"use client";

import { getPaymentAppData } from "@calcom/app-store/_utils/payments/getPaymentAppData";
import type { BookerEvent } from "@calcom/features/bookings/types";
import { convertFromSmallestToPresentableCurrencyUnit } from "@calcom/lib/currencyConversions";
import { useEffect } from "react";

import { metaPixel } from "./client";
import { posthogAnalytics } from "../posthogTracking/client";
import { googleAnalytics } from "../googleTracking/client";

const locationLabel = (event: BookerEvent) => {
  const firstLocation = event.locations?.[0];
  if (typeof firstLocation === "string") return firstLocation;
  if (firstLocation && typeof firstLocation === "object") {
    const value = firstLocation as Record<string, unknown>;
    return [value.address, value.optionValue, value.displayLocation].find(
      (candidate): candidate is string => typeof candidate === "string" && candidate.length > 0
    );
  }
  return undefined;
};

export function MetaBookingFunnel({
  event,
  selectedTimeslot,
  bookingState,
}: {
  event?: BookerEvent | null;
  selectedTimeslot?: string | null;
  bookingState: string;
}) {
  useEffect(() => {
    if (!event?.id || !event.title) return;
    const payment = getPaymentAppData(event);
    const currency = (payment.currency || event.currency || "CAD").toUpperCase();
    const value = convertFromSmallestToPresentableCurrencyUnit(payment.price || event.price || 0, currency);
    const parameters = {
      content_name: event.title,
      content_category: "Photography Session",
      content_ids: [String(event.id)],
      content_type: "product",
      value,
      currency,
    };
    if (metaPixel.isConfigured()) {
      metaPixel.once(`view-content:${event.id}`, () => metaPixel.trackStandardEvent("ViewContent", parameters));
    }
    if (posthogAnalytics.isConfigured()) {
      posthogAnalytics.once(`view-content:${event.id}`, () =>
        posthogAnalytics.capture("booking_view_content", {
          event_type_id: event.id,
          event_type_title: event.title,
          value,
          currency,
        })
      );
    }
  }, [event]);

  useEffect(() => {
    if (!event?.id || !selectedTimeslot) return;
    const payment = getPaymentAppData(event);
    const currency = (payment.currency || event.currency || "CAD").toUpperCase();
    const value = convertFromSmallestToPresentableCurrencyUnit(payment.price || event.price || 0, currency);
    const base = {
      content_name: event.title,
      content_category: "Photography Session",
      content_ids: [String(event.id)],
      content_type: "product",
      value,
      currency,
    };
    const location = locationLabel(event);
    if (metaPixel.isConfigured()) {
      metaPixel.once(`initiate-checkout:${event.id}`, () =>
        metaPixel.trackStandardEvent("InitiateCheckout", { ...base, num_items: 1 })
      );
      metaPixel.once(`schedule:${event.id}`, () =>
        metaPixel.trackStandardEvent("Schedule", {
          content_name: event.title,
          content_ids: [String(event.id)],
          value,
          currency,
          location,
        })
      );
    }
    if (posthogAnalytics.isConfigured()) {
      posthogAnalytics.once(`initiate-checkout:${event.id}`, () =>
        posthogAnalytics.capture("booking_initiate_checkout", {
          event_type_id: event.id,
          event_type_title: event.title,
          value,
          currency,
        })
      );
      posthogAnalytics.once(`schedule:${event.id}`, () =>
        posthogAnalytics.capture("booking_schedule", {
          event_type_id: event.id,
          event_type_title: event.title,
          location,
          value,
          currency,
        })
      );
    }
  }, [event, selectedTimeslot]);

  useEffect(() => {
    if (!event?.id || bookingState !== "booking") return;
    const payment = getPaymentAppData(event);
    if (!payment.price || payment.price <= 0) return;
    const currency = (payment.currency || event.currency || "CAD").toUpperCase();
    const value = convertFromSmallestToPresentableCurrencyUnit(payment.price || event.price || 0, currency);
    const properties = {
      content_name: event.title,
      content_ids: [String(event.id)],
      content_type: "product",
      value,
      currency,
    };
    if (metaPixel.isConfigured()) {
      metaPixel.once(`add-payment-info:${event.id}`, () => metaPixel.trackStandardEvent("AddPaymentInfo", properties));
    }
    if (posthogAnalytics.isConfigured()) {
      posthogAnalytics.once(`add-payment-info:${event.id}`, () =>
        posthogAnalytics.capture("booking_add_payment_info", {
          event_type_id: event.id,
          event_type_title: event.title,
          value,
          currency,
        })
      );
    }
  }, [bookingState, event]);

  return null;
}

export function MetaPaymentStep({
  eventTypeId,
  eventTypeTitle,
  amount,
  currency,
}: {
  eventTypeId: number;
  eventTypeTitle: string;
  amount: number;
  currency: string;
}) {
  useEffect(() => {
    if (!metaPixel.isConfigured()) return;
    metaPixel.once(`add-payment-info:${eventTypeId}`, () =>
      metaPixel.trackStandardEvent("AddPaymentInfo", {
        content_name: eventTypeTitle,
        content_ids: [String(eventTypeId)],
        content_type: "product",
        value: convertFromSmallestToPresentableCurrencyUnit(amount, currency),
        currency: currency.toUpperCase(),
      })
    );
  }, [amount, currency, eventTypeId, eventTypeTitle]);
  return null;
}

export function MetaVerifiedPurchase({
  bookingUid,
  eventTypeId,
  eventTypeTitle,
  amount,
  currency,
  verifiedPaid,
}: {
  bookingUid: string;
  eventTypeId: number;
  eventTypeTitle: string;
  amount: number;
  currency: string;
  verifiedPaid: boolean;
}) {
  useEffect(() => {
    if (!verifiedPaid) return;
    const eventId = `purchase_${bookingUid}`;
    const value = convertFromSmallestToPresentableCurrencyUnit(amount, currency);
    if (metaPixel.isConfigured()) {
      metaPixel.once(eventId, () =>
        metaPixel.trackStandardEvent(
          "Purchase",
          {
            content_name: eventTypeTitle,
            content_category: "Photography Session",
            content_ids: [String(eventTypeId)],
            content_type: "product",
            value,
            currency: currency.toUpperCase(),
            order_id: bookingUid,
            num_items: 1,
          },
          eventId
        )
      );
    }
    if (posthogAnalytics.isConfigured()) {
      posthogAnalytics.once(`purchase:${bookingUid}`, () =>
        posthogAnalytics.capture(
          "booking_purchase",
          {
            event_type_id: eventTypeId,
            event_type_title: eventTypeTitle,
            value,
            currency: currency.toUpperCase(),
          },
          eventId
        )
      );
    }
    if (googleAnalytics.isConfigured()) {
      googleAnalytics.oncePersistent(`purchase:${bookingUid}`, () =>
        googleAnalytics.purchase({
          bookingUid,
          eventTypeId,
          eventTypeTitle,
          value,
          currency,
        })
      );
    }
  }, [amount, bookingUid, currency, eventTypeId, eventTypeTitle, verifiedPaid]);
  return null;
}
